// Custom Select that allows HTML in the options and selected option.
// Makes use of the npm package `custom-select`'s CSS. (https://www.npmjs.com/package/custom-select)

import PerfectScrollbar from 'perfect-scrollbar';
import { Maybe } from 'monet';
import Popper from 'popper.js'
import shortid from 'shortid';

import getTransitionEvent from '@H/transitionEvent';
import { addClass, removeClass, toggleClass } from '@H/class-list';

let settings = {
    options: []
};

let customSelect;
let transitionEvent;
window.poppers = {};

function _createPanel(id, mPanelClass) {
    const customSelectPanelWrapper = document.createElement('div');
    const customSelectPanel = document.createElement('div');
    customSelectPanelWrapper.style.width = `${customSelect.offsetWidth}px`;
    addClass(customSelectPanelWrapper, 'custom-select-panel-wrapper');
    mPanelClass.map(cls => {
        addClass(customSelectPanelWrapper, cls);
        return true;
    });
    customSelectPanelWrapper.setAttribute('data-panel-id', id);
    addClass(customSelectPanel, 'custom-select-panel');

    settings.options.forEach((opt, index) => {
        customSelectPanel.appendChild(_createOption(opt, index));
    });

    if (settings.config && settings.config.withDivider) {
        let preferred = customSelectPanel.querySelectorAll('.preferred');
        let divider = document.createElement('div');
        addClass(divider, 'divider');
        divider.setAttribute('role', 'separator');
        divider.setAttribute('aria-disabled', true);
        preferred[preferred.length - 1].insertAdjacentElement(
            'afterend',
            divider
        );
    }

    customSelectPanelWrapper.appendChild(customSelectPanel);
    document.getElementById('popperContainer').appendChild(customSelectPanelWrapper);
    settings.panelWrapper = customSelectPanelWrapper;
    settings.panel = customSelectPanel;
}

function _updatePanel(customSelect) {
    const id = customSelect.getAttribute('data-id');
    const customSelectPanel = document.querySelector(`[data-panel-id="${id}"]`).querySelector('.custom-select-panel');

    customSelectPanel.innerHTML = "";

    settings.options.forEach((opt, index) => {
        customSelectPanel.appendChild(_createOption(opt, index));
    });
}

function _createOpener(select, customSelectOpener = null) {
    if (!customSelectOpener) {
      customSelectOpener = document.createElement('span');
      addClass(customSelectOpener, 'custom-select-opener');
    }

    let openerInner = document.createElement('span');
    let anySelected = settings.options.filter(opt => opt.selected);
    if (anySelected.length > 0) {
        openerInner.innerHTML = anySelected[0].content;
        customSelectOpener.setAttribute('data-value', anySelected[0].value);
        select.value = anySelected[0].value;
    } else {
        openerInner.innerHTML = settings.options[0].content;
        customSelectOpener.setAttribute(
            'data-value',
            settings.options[0].value
        );
        select.value = settings.options[0].value;
    }

    customSelectOpener.appendChild(openerInner);
    settings.opener = customSelectOpener;
    customSelect.appendChild(customSelectOpener);
}

function _createOption(option, index) {
    let customSelectOption = document.createElement('div');
    addClass(customSelectOption, 'custom-select-option');
    if (option.classes) {
        Array.from(option.classes).forEach(val =>
            addClass(customSelectOption, val)
        );
    }

    if (option.dataCode) {
        customSelectOption.setAttribute('data-code', option.dataCode);
    }

    if (settings.options.indexOf(option) == 0) {
        addClass(customSelectOption, 'is-selected');
    }

    customSelectOption.setAttribute('data-value', option.value);
    customSelectOption.innerHTML = settings.options[index].content;

    return customSelectOption;
}

function _createWrapper() {
    customSelect = document.createElement('div');
    addClass(customSelect, 'customSelect custom-select-container');
}

function _setOptions(originalOptions) {
    const uniqueValues = [];

    settings.options = [...originalOptions].map(opt => {
        if (uniqueValues.includes(opt.value)) return false;

        uniqueValues.push(opt.value);

        let item = {
            value: opt.value,
            content: opt.innerHTML,
            classes: opt.classList ? opt.classList : null
        };

        if (opt.getAttribute('data-selected')) {
            item.selected = opt.getAttribute('data-selected');
        }

        if (opt.getAttribute('data-code')) {
            item.dataCode = opt.getAttribute('data-code');
        }

        return item;
    });
}

function _insertCustomSelect() {
    let parent = settings.original.parentNode;
    parent.insertBefore(customSelect, settings.original);
}

function _grabId(el) {
    const select = el.closest('[data-id]');
    const panel = el.closest('[data-panel-id]');
    return select ? select.getAttribute('data-id') : panel ? panel.getAttribute('data-panel-id') : null;
}

function _addEvents(id) {
    const targetPanelWrapper = document.querySelector(`[data-panel-id="${id}"]`);
    const targetPanel = targetPanelWrapper.querySelector('.custom-select-panel');
    const targetSelect = document.querySelector(`[data-id="${id}"]`);
    const opener = targetSelect.querySelector('.custom-select-opener');
    const panel = targetPanel;

    let scrollbar = new PerfectScrollbar(panel, {
        minScrollbarLength: 40,
        suppressScrollX: true,
        wheelPropagation: true
    });

    transitionEvent &&
        panel.addEventListener(transitionEvent, e => {
            if (e.propertyName == 'max-height') {
                scrollbar.update();
            }
        });

    // Handles opening the select
    opener.addEventListener('click', e => {
        const currentId = _grabId(e.target);
        if (currentId) {
            const targetSelect = document.querySelector(`[data-id="${currentId}"]`);
            const targetPanelWrapper = document.querySelector(`[data-panel-id="${currentId}"]`);
            const targetPanel = targetPanelWrapper.querySelector('.custom-select-panel');

            poppers[`popper-${currentId}`].scheduleUpdate();
            toggleClass(targetSelect, 'is-open');
            targetSelect.setAttribute('data-placement', targetPanelWrapper.getAttribute('x-placement'));
            targetPanelWrapper.style.width = `${targetSelect.getBoundingClientRect().width}px`;
            toggleClass(targetPanelWrapper, 'is-open');
            toggleClass(targetPanel, 'select-is-open');
        }
    });

    // Handles option click
    panel.addEventListener('click', e => {
        if (e.target.classList.contains('custom-select-option')) {
            const currentId = _grabId(e.target);
            if (currentId) {
                const opt = e.target;
                const targetSelect = document.querySelector(`[data-id="${currentId}"]`);
                const targetPanelWrapper = document.querySelector(`[data-panel-id="${currentId}"]`);
                const targetPanel = targetPanelWrapper.querySelector('.custom-select-panel');
                const innerSelect = targetSelect.querySelector('select');
                const targetOpener = targetSelect.querySelector('.custom-select-opener');
                const value = opt.getAttribute('data-value');

                innerSelect.value = value;
                // Trigger change for validation
                innerSelect.dispatchEvent(new Event('change'));
                removeClass(targetSelect, 'is-open');
                removeClass(targetPanelWrapper, 'is-open');
                removeClass(targetPanel, 'select-is-open');

                targetOpener.innerHTML = opt.innerHTML;
                targetOpener.setAttribute('data-value', value);
            }
        }
    });

    // Handles outside of select click
    document.body.addEventListener('click', e => {
        const customSelects = document.querySelectorAll('.customSelect');
        if (customSelects.length) {
            Array.from(customSelects).forEach(sel => {
                if (!sel.contains(e.target)) {
                    const thisId = sel.getAttribute('data-id');
                    const targetPanelWrapper = document.querySelector(`[data-panel-id="${thisId}"]`);
                    const targetPanel = targetPanelWrapper.querySelector('.custom-select-panel');

                    removeClass(sel, 'is-open');
                    removeClass(targetPanelWrapper, 'is-open');
                    removeClass(targetPanel, 'select-is-open');5
                }
            });
        }
    });
}

function _initPopper() {
    return new Popper(customSelect, settings.panelWrapper, {
        placement: 'top',
        positionFixed: true,
        removeOnDestroy: true,
        onCreate(data) {
            const panel = data.instance.popper;
            const reference = data.instance.reference;
            if (panel && reference) {
                reference.setAttribute('data-placement', panel.getAttribute('x-placement'));
            }
        },
        onUpdate(data) {
            const panel = data.instance.popper;
            const reference = data.instance.reference;
            if (panel && reference) {
                reference.setAttribute('data-placement', panel.getAttribute('x-placement'));
            }
        }
    });
}

function getPopperElement(id) {
    const popper = window.poppers[`popper-${id}`];

    if (!popper) {
        return null;
    }

    return popper.reference;
}

function hidePopper(popper) {
    const popperEl = popper.popper;
    const referenceEl = popper.reference;

    removeClass(referenceEl, 'is-open');
    removeClass(popperEl, 'is-open');
    removeClass(popperEl.querySelector('.custom-select-panel'), 'select-is-open');
}

function updateSelectOptions(select) {
  customSelect = select.parentElement;
  let opener = customSelect.querySelector('.custom-select-opener');

  opener.innerHTML = "";

  settings.config = {};

  _setOptions(select.options);
  _createOpener(select, opener);
  _updatePanel(customSelect);
}

function createCustomSelect(select, config = {}) {
    const currentId = shortid.generate();
    settings = {};
    settings.original = select;

    if (Object.keys(config).length) {
        settings.config = config;
    }

    transitionEvent = getTransitionEvent();
    _setOptions(select.options);
    _createWrapper();
    _insertCustomSelect();
    customSelect.appendChild(select);
    _createOpener(select);
    _createPanel(currentId, Maybe.fromUndefined(config.panelClass));
    customSelect.setAttribute('data-id', currentId);
    window.poppers[`popper-${currentId}`] = _initPopper();
    _addEvents(currentId);

    return currentId;
}

export { createCustomSelect, updateSelectOptions, hidePopper, getPopperElement };
export default createCustomSelect;



// WEBPACK FOOTER //
// ./resources/assets/js/helpers/custom-select.js